import { useQueryClient } from '@tanstack/react-query';
import { router } from 'expo-router';
import { useState } from 'react';
import { Keyboard, Pressable } from 'react-native';
import { type SceneRendererProps, TabView } from 'react-native-tab-view';
import {
    BackButton,
    BackButtonIconType,
    BodyMediumRegular,
    FullScreenSpinner,
    isWeb,
    SafeAreaView,
    ScrollView,
    useToastController,
    View,
    YStack,
} from 'smoothie';

import { useUserAuthorization } from '../../../auth/hooks/useUserAuthorization';
import { LimitedAccessScope } from '../../../auth/types/UserAuthorization';
import { isEmpty } from '../../../common/misc';
import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import LogRocketFacade from '../../../logger/log-rocket/LogRocketFacade';
import { captureException } from '../../../logger/Logger';
import { useUser } from '../../../user/providers/UserProvider';
import useRunKYCVerify, {
    type RunKYCVerifyBody,
} from '../../hooks/kyc/useRunKycVerify';
import { KYCVerificationStatus } from '../../types/BankAccount';
import {
    getBankingDisclosures,
    HYS_ACCOUNT_OPEN_POLICY_DISCLOSURES,
} from '../Disclosures';
import Address from './steps/Address';
import Approved from './steps/Approved';
import ContactInfo from './steps/ContactInfo';
import Dob from './steps/Dob';
import Explainer from './steps/Explainer';
import Review from './steps/Review';
import Ssn from './steps/Ssn';
import UnderReview from './steps/UnderReview';

enum KycStep {
    EXPLAINER = 'explainer',
    CONTACT_INFO = 'contactInfo',
    SSN = 'ssn',
    DOB = 'dob',
    ADDRESS = 'address',
    REVIEW = 'review',
    APPROVED = 'approved',
    UNDER_REVIEW = 'underReview',
}

const Kyc = () => {
    LogRocketFacade.tagPage('Legacy KYC');

    const toast = useToastController();
    const queryClient = useQueryClient();

    const user = useAuthedUser();
    const { refetch } = useUser();
    const { limitedAccessScope } = useUserAuthorization();

    const routes = Object.values(KycStep).map((step) => ({ key: step }));

    const reviewIndex = routes.findIndex(({ key }) => key === KycStep.REVIEW);
    const approvedIndex = routes.findIndex(
        ({ key }) => key === KycStep.APPROVED,
    );
    const underReviewIndex = routes.findIndex(
        ({ key }) => key === KycStep.UNDER_REVIEW,
    );

    const [index, setIndex] = useState(0);
    const [kycData, setKycData] = useState<Partial<RunKYCVerifyBody>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { mutateAsync: runKYCVerify } = useRunKYCVerify();

    const onSubmit = async () => {
        setIsSubmitting(true);

        try {
            const verifiedUser = await runKYCVerify({
                body: {
                    ...(kycData as RunKYCVerifyBody),
                    disclosures: getBankingDisclosures({
                        withKyc: true,
                        policyTypes: HYS_ACCOUNT_OPEN_POLICY_DISCLOSURES,
                    }),
                },
                urlParams: {
                    userId: user.id,
                },
            });

            // Wrap this in a timeout to wait for the webhook to settle on the backend
            setTimeout(async () => {
                // Invalidate the query cache for the user because KYC status and joint onboarding status has now changed
                await queryClient.invalidateQueries({
                    queryKey: [`getCustomer/${user.stytchId}`],
                });
                void refetch?.();

                if (
                    verifiedUser.syncteraKycStatus ===
                        KYCVerificationStatus.ACCEPTED &&
                    verifiedUser.membership == null
                ) {
                    // TODO, HYS joint account holder only - LOW PRIORITY - for a joint account holder this doesn't work because limitedAccessScope isn't refreshed
                    // A hard refresh of the browser works around the bug
                    router.replace('/banking');
                } else if (
                    verifiedUser.syncteraKycStatus ===
                    KYCVerificationStatus.ACCEPTED
                ) {
                    setIndex(approvedIndex);
                } else {
                    setIndex(underReviewIndex);
                }

                setIsSubmitting(false);
            }, 3000);
        } catch (e) {
            captureException(e);
            setIsSubmitting(false);
            toast.show('Something went wrong');
        }
    };

    const onPressNext = (formData?: Partial<RunKYCVerifyBody>) => {
        formData != null &&
            setKycData({
                ...kycData,
                ...formData,
            });
        setIndex(index + 1);
    };

    const onPressBack = () => {
        if (index > 0) {
            setIndex(index - 1);
        } else if ([approvedIndex, underReviewIndex].includes(index)) {
            router.replace('/home/banking');
        } else {
            if (router.canGoBack()) {
                router.back();
            } else {
                router.replace('/home/banking');
            }
        }
    };

    const renderScene = ({
        route,
    }: SceneRendererProps & { route: { key: KycStep } }) => {
        if (routes[index]?.key !== route.key) {
            return null;
        }

        const Content = (() => {
            switch (route.key) {
                case KycStep.EXPLAINER:
                    return <Explainer onPressNext={onPressNext} />;
                case KycStep.CONTACT_INFO:
                    return <ContactInfo onPressNext={onPressNext} />;
                case KycStep.SSN:
                    return <Ssn onPressNext={onPressNext} />;
                case KycStep.DOB:
                    return <Dob onPressNext={onPressNext} />;
                case KycStep.ADDRESS:
                    return <Address onPressNext={onPressNext} />;
                case KycStep.REVIEW:
                    return (
                        <Review
                            kycData={kycData as RunKYCVerifyBody}
                            onPressSubmit={onSubmit}
                        />
                    );
                case KycStep.APPROVED:
                    return <Approved />;
                case KycStep.UNDER_REVIEW:
                    return <UnderReview />;
                default:
                    return <></>;
            }
        })();

        return (
            <ScrollView
                flex={1}
                contentContainerStyle={{ flexGrow: 1 }}
                paddingTop={12}
                paddingHorizontal={16}
                $sm={{
                    width: 500,
                    alignSelf: 'center',
                    marginTop: 50,
                }}
            >
                {Content}
            </ScrollView>
        );
    };

    const getTabTitle = (index: number): string => {
        switch (index) {
            case 0:
                return 'Opening an account';
            case reviewIndex:
                return 'Review';
            case approvedIndex:
            case underReviewIndex:
                return '';
            default:
                return 'Identification confirmation';
        }
    };

    if (isSubmitting) {
        return <FullScreenSpinner />;
    }

    return (
        <Pressable
            onPress={Keyboard.dismiss}
            style={{ flex: 1 }}
            disabled={isWeb}
        >
            <SafeAreaView style={{ flex: 1 }}>
                <YStack flex={1}>
                    <TabView
                        swipeEnabled={false}
                        navigationState={{ index, routes }}
                        renderTabBar={() => (
                            <View paddingVertical={20}>
                                <BodyMediumRegular textAlign="center">
                                    {getTabTitle(index)}
                                </BodyMediumRegular>
                                {limitedAccessScope !==
                                    LimitedAccessScope.KYC && (
                                    <View
                                        alignSelf="flex-start"
                                        marginTop={
                                            isEmpty(getTabTitle(index))
                                                ? 0
                                                : -32
                                        }
                                        paddingLeft={16}
                                    >
                                        <BackButton
                                            customBackFn={onPressBack}
                                            iconType={
                                                [
                                                    approvedIndex,
                                                    underReviewIndex,
                                                ].includes(index)
                                                    ? BackButtonIconType.X
                                                    : BackButtonIconType.ARROW_LEFT
                                            }
                                        />
                                    </View>
                                )}
                            </View>
                        )}
                        renderScene={renderScene}
                        onIndexChange={setIndex}
                    />
                </YStack>
            </SafeAreaView>
        </Pressable>
    );
};

export default Kyc;
